import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
  Alert
} from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import { CustomBreadcrumbs, PageBanner, SubmitExamModal, SuccessModal, ExamResultModal, ExamExpiredModal, LatexRenderer, SubmitProgressModal } from '../../common';
import teacherBannerBg from '../../../assets/images/teacher.jpg';
import dangerIcon from '../../../assets/images/SVG/dangerIcon.svg';
import EssayExamPaperSection from './EssayExamPaperSection';
import examService from '../../../infrastructure/services/examService';
import { initiateExamSocket } from '../../../infrastructure/services/notificationService';
import { getSynchronizedTime, parseSriLankanDateTime } from '../../../infrastructure/services/timeSyncService';
import { getCookie } from '../../../infrastructure/utils/cookieStorage';

const getQuestionImageStyles = (size) => {
  const normalized = String(size || 'medium').toLowerCase();
  if (normalized === 'small') {
    return { maxWidth: '200px', maxHeight: '160px' };
  }
  if (normalized === 'large') {
    return { maxWidth: '500px', maxHeight: '400px' };
  }
  return { maxWidth: '350px', maxHeight: '280px' };
};

const UPLOAD_GRACE_SECONDS = 30 * 60;
const PDF_UPLOAD_ERROR = 'Only PDF files are allowed.';

const getDisconnectMessage = (err) => {
  const backendMsg = err?.data?.message || err?.message || '';
  if (backendMsg.includes('session_active_on_another_device')) {
    return 'This session has been disconnected because you logged in or attended from another device/IP.';
  }
  return backendMsg || 'This session has been disconnected because you logged in or attended from another device/IP.';
};

const isPdfFile = (file) => {
  if (!file) return false;
  const fileType = String(file.type || '').toLowerCase();
  const fileName = String(file.name || '').toLowerCase();
  return fileType === 'application/pdf' || (!fileType && fileName.endsWith('.pdf'));
};

const resetFileInput = (target) => {
  if (target && 'value' in target) {
    target.value = '';
  }
};

const getAuthToken = () => {
  return getCookie('student_auth_token') || getCookie('authToken') || sessionStorage.getItem('student_auth_token') || sessionStorage.getItem('authToken') || localStorage.getItem('student_auth_token') || localStorage.getItem('authToken');
};

export default function ExamPaperPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { classExamId: paramExamId } = useParams();
  const exam = location.state?.exam;
  const [resolvedExamInfo, setResolvedExamInfo] = useState(exam);

  const examIdFromUrl = paramExamId || resolvedExamInfo?.id || resolvedExamInfo?.examId;
  const examType = (resolvedExamInfo?.type || '').toUpperCase();
  const childExamId = resolvedExamInfo?.childExamId;

  useEffect(() => {
    if (!resolvedExamInfo && examIdFromUrl) {
      const getDetails = async () => {
        try {
          const res = await examService.getExamDetails(examIdFromUrl);
          if (res && res.isSuccessful && res.content) {
            const details = res.content;
            setResolvedExamInfo({
              id: paramExamId,
              examId: details.examId,
              childExamId: details.examId,
              name: details.examTitle,
              type: details.examType === 'mcq' ? 'MCQ' : details.examType === 'essay' ? 'Essay' : 'Structure',
              duration: details.duration,
              subject: details.subjectName,
              teacher: details.teacherName,
              teacherId: details.teacherId
            });
          }
        } catch (e) {
          console.error('Failed to resolve exam details for refresh:', e);
        }
      };
      getDetails();
    }
  }, [resolvedExamInfo, examIdFromUrl, paramExamId]);

  const EXAM_MANAGER_TEACHER_ID_KEY = 'examManagerTeacherId';

  const returnToCurrentExams = React.useCallback(() => {
    const teacherId = exam?.teacherId || sessionStorage.getItem(EXAM_MANAGER_TEACHER_ID_KEY);
    if (teacherId) {
      navigate(`/exam-manager/exams/${teacherId}/current`, {
        state: { refreshExams: getSynchronizedTime() }
      });
    } else {
      navigate('/exam-manager');
    }
  }, [exam?.teacherId, navigate]);

  React.useEffect(() => {
    if (exam?.teacherId) {
      sessionStorage.setItem(EXAM_MANAGER_TEACHER_ID_KEY, exam.teacherId);
    }
  }, [exam?.teacherId]);

  const breadcrumbItems = [
    { label: 'Home', href: '/dashboard' },
    { label: 'Exam Management', href: '/exam-manager' },
    { label: 'Take Exam' }
  ];

  const parseDurationStr = (durationStr) => {
    if (!durationStr) return 5400;
    const hMatch = durationStr.match(/(\d+)\s*[hH]/);
    const mMatch = durationStr.match(/(\d+)\s*[mM]/);
    const h = hMatch ? parseInt(hMatch[1]) : 0;
    const m = mMatch ? parseInt(mMatch[1]) : 0;
    return h * 3600 + m * 60 || 5400;
  };

  const getDisplayExamTitle = (title) => {
    const plainTitle = String(title || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (!plainTitle) return 'Exam';
    if (plainTitle.length <= 60) return plainTitle;

    const shortTitle = plainTitle.split(' ').slice(0, 10).join(' ').trim();
    return `${shortTitle || plainTitle.slice(0, 60).trim()}...`;
  };

  const [timeLeft, setTimeLeft] = useState(() => parseDurationStr(exam?.duration));
  const [questions, setQuestions] = useState([]);
  const [examLoading, setExamLoading] = useState(!!examIdFromUrl);
  const [activeIdx, setActiveIdx] = useState(0);

  const examStartedAtRef = React.useRef(null);
  const totalDurationSecRef = React.useRef(parseDurationStr(exam?.duration));
  const uploadDeadlineRef = React.useRef(null);

  const [isExamTimeOver, setIsExamTimeOver] = useState(false);
  const [uploadTimeLeft, setUploadTimeLeft] = useState(UPLOAD_GRACE_SECONDS);
  const isExamTimeOverRef = React.useRef(false);
  const mcqExpiredHandledRef = React.useRef(false);
  const essayCountdownRef = React.useRef(null);

  const ensureUploadDeadline = React.useCallback(() => {
    if (uploadDeadlineRef.current) return;
    if (examStartedAtRef.current && totalDurationSecRef.current) {
      uploadDeadlineRef.current =
        examStartedAtRef.current + (totalDurationSecRef.current + UPLOAD_GRACE_SECONDS) * 1000;
    } else {
      uploadDeadlineRef.current = getSynchronizedTime() + UPLOAD_GRACE_SECONDS * 1000;
    }
  }, []);

  // Question navigation timer refs
  const activeQuestionStartTimeRef = React.useRef(getSynchronizedTime());
  const prevActiveIdxRef = React.useRef(0);
  const activeSessionTokenRef = React.useRef('');
  const socketRef = React.useRef(null);
  const handleSubmitConfirmRef = React.useRef(null);
  const examLoadingRef = React.useRef(true);
  const timerBaseRef = React.useRef(null);
  const initialTimeLeftRef = React.useRef(0);
  const uploadTimerBaseRef = React.useRef(null);
  const initialUploadTimeLeftRef = React.useRef(0);
  const hasJoinedRef = React.useRef(false);
  const [answers, setAnswers] = useState({});
  const answersRef = React.useRef(answers);
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);
  useEffect(() => {
    handleSubmitConfirmRef.current = handleSubmitConfirm;
  });
  useEffect(() => {
    examLoadingRef.current = examLoading;
  }, [examLoading]);
  const [flagged, setFlagged] = useState({});
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Offline and Socket restriction states
  const [showForceDisconnectModal, setShowForceDisconnectModal] = useState(false);
  const [forceDisconnectMessage, setForceDisconnectMessage] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);

  const syncOfflineAnswers = React.useCallback(async () => {
    if (!activeSessionTokenRef.current) {
      return;
    }

    const key = `offline_answers_${examIdFromUrl}`;
    const queuedDataStr = localStorage.getItem(key);
    if (!queuedDataStr) return;

    try {
      const queuedData = JSON.parse(queuedDataStr);
      if (Array.isArray(queuedData) && queuedData.length > 0) {
        setIsSyncing(true);
        let hasConflict = false;
        for (const item of queuedData) {
          try {
            await examService.saveQuestionAnswer(examIdFromUrl, {
              questionId: item.questionId,
              answer: item.answer,
              durationSeconds: item.durationSeconds
            }, activeSessionTokenRef.current);
          } catch (e) {
            console.error('[Exam Session] Failed to sync item:', item, e);
            if (e?.message?.includes('session_active_on_another_device') || e?.status === 409 || e?.data?.error_code === 'session_active_on_another_device') {
              setForceDisconnectMessage(getDisconnectMessage(e));
              setShowForceDisconnectModal(true);
              hasConflict = true;
              break;
            }
          }
        }
        if (!hasConflict) {
          localStorage.removeItem(key);
        }
      }
    } catch (e) {
      console.error('Error syncing offline progress:', e);
    } finally {
      setIsSyncing(false);
    }
  }, [examIdFromUrl]);

  // Online/Offline listeners
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncOfflineAnswers();
    };
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if (navigator.onLine) {
      syncOfflineAnswers();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncOfflineAnswers]);

  // Socket connection to restrict multiple device/IP attendance
  useEffect(() => {
    const token = getAuthToken();
    if (!token) return undefined;

    let socket;
    try {
      socket = initiateExamSocket(token, (data) => {
        setForceDisconnectMessage(data?.message || 'This session has been disconnected because you logged in or attended from another device/IP.');
        setShowForceDisconnectModal(true);
      });
      socketRef.current = socket;

      socket.on('connect', () => {
        const activeToken = getAuthToken();
        if (socket && activeToken && examIdFromUrl && !examLoadingRef.current && !hasJoinedRef.current) {
          console.log('[Exam Session] Socket reconnected, rejoining session...');
          socket.emit('join-exam-session', { token: activeToken, classExamId: examIdFromUrl });
          hasJoinedRef.current = true;
        }
      });

      socket.on('disconnect', () => {
        console.log('[Exam Session] Socket disconnected');
        hasJoinedRef.current = false;
      });

      socket.on('times-up', (data) => {
        console.log('[Exam Session] Time is up event received:', data);
        handleExamExpired(true);
        if (handleSubmitConfirmRef.current) {
          handleSubmitConfirmRef.current(true);
        }
      });
    } catch (e) {
      console.error('Failed to initiate exam socket connection:', e);
    }

    return () => {
      if (socket) {
        socket.disconnect();
        socketRef.current = null;
      }
      hasJoinedRef.current = false;
    };
  }, [examIdFromUrl]);

  // Join exam session room once loaded
  useEffect(() => {
    const token = getAuthToken();
    if (!examLoading && socketRef.current && token && examIdFromUrl && !hasJoinedRef.current) {
      console.log('[Exam Session] Exam loaded, rejoining/joining exam session...');
      socketRef.current.emit('join-exam-session', { token, classExamId: examIdFromUrl });
      hasJoinedRef.current = true;
    }
  }, [examLoading, examIdFromUrl]);

  // Redirect to exam-manager if accessed directly without exam state
  useEffect(() => {
    if (!examIdFromUrl) {
      window.location.replace('/exam-manager');
    }
  }, [examIdFromUrl]);

  // Warn when refreshing, closing tab or leaving page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (showSuccessModal || showResultModal || showExpiredModal || examLoading || isSubmitting) {
        return;
      }
      e.preventDefault();
      e.returnValue = 'Are you sure you want to cancel this quiz? If you cancel, your progress will not be saved.';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [showSuccessModal, showResultModal, showExpiredModal, examLoading, isSubmitting]);

  // Intercept navigation links click to show custom confirmation dialog
  useEffect(() => {
    const handleCaptureClick = (event) => {
      if (showSuccessModal || showResultModal || showExpiredModal || examLoading || isSubmitting) {
        return;
      }

      const target = event.target;

      // 1. Sidebar navigation items and Logo
      const navItem = target.closest('.nav-item');
      const isSidebarNav = navItem && !target.closest('.sidebar-expand-btn') && !target.closest('.sidebar-close');
      const isSidebarLogo = target.closest('.sidebar-logo');

      // 2. Breadcrumbs
      const isBreadcrumb = target.closest('.MuiBreadcrumbs-li') && (
        target.textContent.includes('Home') ||
        target.textContent.includes('Exam Management')
      );

      // 3. Profile widget (avatar/name block at top right)
      const isProfileWidget = target.closest('[role="button"]') && (
        target.closest('[role="button"]').textContent.includes('student') ||
        target.closest('[role="button"]').querySelector('.MuiAvatar-root') ||
        target.closest('.MuiAvatar-root')
      );

      // 4. Profile dropdown menu items
      const isProfileMenuItem = target.closest('.MuiMenuItem-root') && (
        target.textContent.includes('Settings') ||
        target.textContent.includes('Logout')
      );

      if (isSidebarNav || isSidebarLogo || isBreadcrumb || isProfileWidget || isProfileMenuItem) {
        let path = '/dashboard';
        const textContent = target.textContent || '';

        if (isSidebarNav) {
          if (textContent.includes('Dashboard')) {
            path = '/dashboard';
          } else if (textContent.includes('Your Exams')) {
            path = '/exam-manager';
          } else if (textContent.includes('Your Result')) {
            path = '/your-result';
          } else if (textContent.includes('Settings') || navItem.title === 'Settings') {
            path = '/settings';
          } else {
            path = '/dashboard';
          }
        } else if (isSidebarLogo) {
          path = '/dashboard';
        } else if (isBreadcrumb) {
          if (textContent.includes('Home')) {
            path = '/dashboard';
          } else if (textContent.includes('Exam Management')) {
            path = '/exam-manager';
          }
        } else if (isProfileWidget) {
          path = '/settings';
        } else if (isProfileMenuItem) {
          if (textContent.includes('Settings')) {
            path = '/settings';
          } else if (textContent.includes('Logout')) {
            path = '/dashboard';
          }
        }

        // Cancel navigation
        event.preventDefault();
        event.stopPropagation();

        setPendingNavigationPath(path);
        setShowExitDialog(true);
      }
    };

    document.addEventListener('click', handleCaptureClick, true);
    return () => {
      document.removeEventListener('click', handleCaptureClick, true);
    };
  }, [showSuccessModal, showResultModal, showExpiredModal, examLoading, isSubmitting]);

  // For Essay/Structured flow
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadError, setUploadError] = useState('');

  // Fetch real questions from the start exam API or start essay exam
  useEffect(() => {
    if (!examIdFromUrl) return;

    const startExamSession = async () => {
      try {
        setExamLoading(true);
        if (examType === 'MCQ') {
          const data = await examService.startExam(examIdFromUrl, childExamId);
          if (data && data.isSuccessful && data.content) {
            const { exam: examMeta, questions: apiQuestions, progress, activeSessionToken } = data.content;
            if (activeSessionToken) {
              activeSessionTokenRef.current = activeSessionToken;
              syncOfflineAnswers();
            }

            // Calculate actual remaining seconds for MCQ (resumed exam)
            const allowedSec = (examMeta.durationHours || 0) * 3600 + (examMeta.durationMinutes || 0) * 60;
            totalDurationSecRef.current = allowedSec || totalDurationSecRef.current;
            if (progress && progress.startedAt) {
              const parsedStart = parseSriLankanDateTime(progress.startedAt);
              examStartedAtRef.current = parsedStart ? parsedStart.getTime() : new Date(progress.startedAt).getTime();
              const elapsedSec = Math.max(0, Math.floor((getSynchronizedTime() - examStartedAtRef.current) / 1000));
              const remainingSec = Math.max(0, allowedSec - elapsedSec);
              setTimeLeft(remainingSec);
              timerBaseRef.current = performance.now();
              initialTimeLeftRef.current = remainingSec;
            } else {
              // Fresh start — mark now as the start time
              examStartedAtRef.current = getSynchronizedTime();
              if (allowedSec > 0) {
                setTimeLeft(allowedSec);
                timerBaseRef.current = performance.now();
                initialTimeLeftRef.current = allowedSec;
              }
            }

            // Map API questions to component format, expanding match-two-sides questions so each left-side item is a separate question page.
            const mapped = [];
            let displayOrder = 1;
            apiQuestions.forEach((q) => {
              if (q.questionType === 'match-two-sides') {
                const leftSide = q.tableJson?.leftSide || [];
                const rightSide = q.tableJson?.rightSide || [];
                const pointsPerItem = leftSide.length > 0 ? q.points / leftSide.length : 0;

                leftSide.forEach((leftItem, idx) => {
                  mapped.push({
                    id: `${q.questionId}_${leftItem.id}`,
                    parentQuestionId: q.questionId,
                    type: 'MatchingSingle',
                    title: `Question ${String(displayOrder++).padStart(2, '0')}`,
                    text: q.questionText || 'Match the following',
                    leftItem: leftItem,
                    rightSideOptions: rightSide,
                    points: pointsPerItem,
                    questionImage: q.questionImage,
                    tableJson: q.tableJson
                  });
                });
              } else {
                let type = 'MCQ';
                if (q.questionType === 'multiple-choice' || q.questionType === 'single-choice') {
                  type = 'MCQ';
                } else if (q.questionType === 'true-false') {
                  type = 'TF';
                } else if (q.questionType === 'fill-in-blanks') {
                  type = 'FillBlank';
                } else if (q.questionType === 'table') {
                  type = 'TableOption';
                }

                mapped.push({
                  id: q.questionId,
                  type,
                  title: `Question ${String(displayOrder++).padStart(2, '0')}`,
                  text: q.questionText,
                  options: (q.answerOptions || [])
                    .sort((a, b) => a.optionOrder - b.optionOrder)
                    .map(o => ({ id: o.optionId, text: o.text, image: o.image, order: o.optionOrder })),
                  points: q.points,
                  questionImage: q.questionImage,
                  tableJson: q.tableJson,
                  givenAnswers: q.givenAnswers
                });
              }
            });

            setQuestions(mapped);

            // Restore previously saved answers on resume
            if (progress && progress.answers && Object.keys(progress.answers).length > 0) {
              const restoredAnswers = {};
              mapped.forEach((q, idx) => {
                if (q.type === 'MatchingSingle') {
                  // MatchingSingle: id is `${parentQuestionId}_${leftItemId}`
                  // The answer is stored in backend as a JSON string under parentQuestionId: { leftItemId: rightItemId }
                  const parentAns = progress.answers[q.parentQuestionId?.toString()];
                  if (parentAns) {
                    try {
                      const parsed = typeof parentAns === 'string' ? JSON.parse(parentAns) : parentAns;
                      const rightSideVal = parsed[q.leftItem.id];
                      if (rightSideVal !== undefined && rightSideVal !== null && rightSideVal !== '') {
                        restoredAnswers[idx] = rightSideVal;
                      }
                    } catch { /* skip */ }
                  }
                } else {
                  const savedAnswer = progress.answers[q.id?.toString()];
                  if (savedAnswer === undefined || savedAnswer === null || savedAnswer === '') return;

                  if (q.type === 'FillBlank') {
                    // Fill-in-blank: stored as plain string
                    restoredAnswers[idx] = savedAnswer;
                  } else if (q.type === 'Matching') {
                    // Matching: stored as JSON string { leftSideId: rightSideId }
                    // Component uses { rightSideId: leftSideId } internally
                    try {
                      const parsed = typeof savedAnswer === 'string' ? JSON.parse(savedAnswer) : savedAnswer;
                      const inverted = {};
                      Object.entries(parsed).forEach(([leftId, rightId]) => {
                        inverted[rightId] = leftId;
                      });
                      restoredAnswers[idx] = inverted;
                    } catch { /* skip malformed */ }
                  } else if (q.type === 'TableOption') {
                    // TableOption: stored as row number string (e.g. "1", "2")
                    restoredAnswers[idx] = parseInt(savedAnswer, 10) || savedAnswer;
                  } else {
                    // MCQ / TF: stored as optionOrder string — find the matching optionId
                    const savedOrder = parseInt(savedAnswer, 10);
                    const matchedOption = q.options && q.options.find(o => o.order === savedOrder);
                    if (matchedOption) {
                      restoredAnswers[idx] = matchedOption.id;
                    } else {
                      restoredAnswers[idx] = savedAnswer;
                    }
                  }
                }
              });
              if (Object.keys(restoredAnswers).length > 0) {
                setAnswers(restoredAnswers);
              }
            }

          }
        } else {
          // Start Essay Exam!
          const data = await examService.startEssayExam(examIdFromUrl, childExamId);
          if (data && data.isSuccessful && data.content) {
            const content = data.content;
            if (content.activeSessionToken) {
              activeSessionTokenRef.current = content.activeSessionToken;
              syncOfflineAnswers();
            }
            const remainingSeconds = content.remainingSeconds;
            if (content.allowedDurationSeconds) {
              totalDurationSecRef.current = content.allowedDurationSeconds;
            }
            if (content.startedAt) {
              const parsedStart = parseSriLankanDateTime(content.startedAt);
              examStartedAtRef.current = parsedStart ? parsedStart.getTime() : new Date(content.startedAt).getTime();
            }
            if (remainingSeconds !== undefined && remainingSeconds !== null) {
              setTimeLeft(remainingSeconds);
              essayCountdownRef.current = remainingSeconds;
              timerBaseRef.current = performance.now();
              initialTimeLeftRef.current = remainingSeconds;
            }
            if (remainingSeconds <= 0) {
              isExamTimeOverRef.current = true;
              ensureUploadDeadline();
              const uploadRemaining = Math.max(0, Math.floor((uploadDeadlineRef.current - getSynchronizedTime()) / 1000));
              setIsExamTimeOver(true);
              setUploadTimeLeft(uploadRemaining);
              uploadTimerBaseRef.current = performance.now();
              initialUploadTimeLeftRef.current = uploadRemaining;
              if (uploadRemaining <= 0) {
                returnToCurrentExams();
                return;
              }
            }
          } else if (data && !data.isSuccessful && data.message === 'You have already submitted this exam') {
            returnToCurrentExams();
            return;
          }
        }
      } catch (err) {
        console.error('Failed to initialize exam session:', err);
      } finally {
        setExamLoading(false);
      }
    };

    startExamSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examIdFromUrl, examType]);

  // Save question answer incrementally on question index transition
  useEffect(() => {
    if (examType !== 'MCQ' || questions.length === 0) return;

    const saveProgress = async (idxToSave) => {
      const q = questions[idxToSave];
      if (!q) return;

      const ans = answersRef.current[idxToSave];
      let formattedAnswer = '';
      let targetQuestionId = q.id.toString();

      if (ans !== undefined && ans !== '') {
        if (q.type === 'MatchingSingle') {
          // Send the combined parent answer map by grouping all answers for the same parentQuestionId
          targetQuestionId = q.parentQuestionId.toString();
          const parentAnswers = {};
          questions.forEach((otherQ, otherIdx) => {
            if (otherQ.parentQuestionId === q.parentQuestionId) {
              const otherAns = answersRef.current[otherIdx];
              if (otherAns !== undefined && otherAns !== '') {
                parentAnswers[otherQ.leftItem.id] = otherAns;
              }
            }
          });
          formattedAnswer = JSON.stringify(parentAnswers);
        } else if (q.type === 'FillBlank' || q.type === 'TableOption') {
          formattedAnswer = ans.toString();
        } else {
          const selectedOpt = q.options.find(o => o.id === ans);
          formattedAnswer = selectedOpt ? selectedOpt.order.toString() : ans.toString();
        }
      } else if (q.type === 'MatchingSingle') {
        // Even if this specific item is cleared, check other sibling items
        targetQuestionId = q.parentQuestionId.toString();
        const parentAnswers = {};
        questions.forEach((otherQ, otherIdx) => {
          if (otherQ.parentQuestionId === q.parentQuestionId) {
            const otherAns = answersRef.current[otherIdx];
            if (otherAns !== undefined && otherAns !== '') {
              parentAnswers[otherQ.leftItem.id] = otherAns;
            }
          }
        });
        if (Object.keys(parentAnswers).length > 0) {
          formattedAnswer = JSON.stringify(parentAnswers);
        }
      }

      const elapsed = Math.round((getSynchronizedTime() - activeQuestionStartTimeRef.current) / 1000);
      if (elapsed > 0) {
        if (!navigator.onLine) {
          const key = `offline_answers_${examIdFromUrl}`;
          const currentQueue = JSON.parse(localStorage.getItem(key) || '[]');
          currentQueue.push({
            questionId: targetQuestionId,
            answer: formattedAnswer,
            durationSeconds: elapsed
          });
          localStorage.setItem(key, JSON.stringify(currentQueue));
        } else {
          try {
            await examService.saveQuestionAnswer(examIdFromUrl, {
              questionId: targetQuestionId,
              answer: formattedAnswer,
              durationSeconds: elapsed
            }, activeSessionTokenRef.current);
            setIsOnline(true);
            syncOfflineAnswers();
          } catch (err) {
            console.error('[Exam Session] Failed to save question progress:', err);
            if (err?.message?.includes('session_active_on_another_device') || err?.status === 409 || err?.data?.error_code === 'session_active_on_another_device') {
              console.warn('[Exam Session] Session conflict detected! Another device/IP has started this exam.');
              setForceDisconnectMessage(getDisconnectMessage(err));
              setShowForceDisconnectModal(true);
            } else if (err?.message === 'Exam expired') {
              handleExamExpired(false);
            } else if (err?.message === 'Exam duration expired' || err?.message === 'You have already completed this exam') {
              handleExamExpired(true);
            } else {
              setIsOnline(false);
              const key = `offline_answers_${examIdFromUrl}`;
              const currentQueue = JSON.parse(localStorage.getItem(key) || '[]');
              currentQueue.push({
                questionId: targetQuestionId,
                answer: formattedAnswer,
                durationSeconds: elapsed
              });
              localStorage.setItem(key, JSON.stringify(currentQueue));
            }
          }
        }
      }
    };

    const prevIdx = prevActiveIdxRef.current;
    if (prevIdx !== activeIdx) {
      saveProgress(prevIdx);
    }

    prevActiveIdxRef.current = activeIdx;
    activeQuestionStartTimeRef.current = getSynchronizedTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx, questions, examIdFromUrl, examType]);

  // MCQ drift-proof timer
  useEffect(() => {
    if (examType !== 'MCQ' || examLoading) return undefined;

    if (timeLeft <= 0) {
      if (!mcqExpiredHandledRef.current) {
        mcqExpiredHandledRef.current = true;
        handleExamExpired(true);
        handleSubmitConfirm(true);
      }
      return undefined;
    }

    const timer = setInterval(() => {
      if (timerBaseRef.current !== null) {
        const elapsedSec = Math.floor((performance.now() - timerBaseRef.current) / 1000);
        const remaining = Math.max(0, initialTimeLeftRef.current - elapsedSec);
        setTimeLeft(remaining);
      } else if (examStartedAtRef.current) {
        const elapsedSec = Math.floor((getSynchronizedTime() - examStartedAtRef.current) / 1000);
        const remaining = Math.max(0, totalDurationSecRef.current - elapsedSec);
        setTimeLeft(remaining);
      } else {
        setTimeLeft((prev) => Math.max(0, prev - 1));
      }
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examType, examLoading, timeLeft]);

  // Essay — continuous wall-clock timer (keeps running after exam ends; no refresh needed)
  useEffect(() => {
    if (examType === 'MCQ' || examLoading) return undefined;

    const tick = () => {
      let examRemaining = 0;

      if (timerBaseRef.current !== null) {
        const elapsedSec = Math.floor((performance.now() - timerBaseRef.current) / 1000);
        examRemaining = Math.max(0, initialTimeLeftRef.current - elapsedSec);
        setTimeLeft(examRemaining);
      } else if (examStartedAtRef.current && totalDurationSecRef.current) {
        const elapsedSec = Math.floor((getSynchronizedTime() - examStartedAtRef.current) / 1000);
        examRemaining = Math.max(0, totalDurationSecRef.current - elapsedSec);
        setTimeLeft(examRemaining);
      } else {
        if (essayCountdownRef.current === null) {
          essayCountdownRef.current = totalDurationSecRef.current;
        }
        essayCountdownRef.current = Math.max(0, essayCountdownRef.current - 1);
        examRemaining = essayCountdownRef.current;
        setTimeLeft(examRemaining);
      }

      if (examRemaining <= 0) {
        if (!isExamTimeOverRef.current) {
          isExamTimeOverRef.current = true;
          setIsExamTimeOver(true);
          ensureUploadDeadline();
          uploadTimerBaseRef.current = performance.now();
          initialUploadTimeLeftRef.current = UPLOAD_GRACE_SECONDS;
        }

        ensureUploadDeadline();
        let uploadRemaining = 0;
        if (uploadTimerBaseRef.current !== null) {
          const elapsedSec = Math.floor((performance.now() - uploadTimerBaseRef.current) / 1000);
          uploadRemaining = Math.max(0, initialUploadTimeLeftRef.current - elapsedSec);
          setUploadTimeLeft(uploadRemaining);
        } else {
          uploadRemaining = Math.max(
            0,
            Math.floor((uploadDeadlineRef.current - getSynchronizedTime()) / 1000)
          );
          setUploadTimeLeft(uploadRemaining);
        }

        if (uploadRemaining <= 0) {
          returnToCurrentExams();
        }
      }
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [examType, examLoading, ensureUploadDeadline, returnToCurrentExams]);

  // Format timer into HH:MM:SS
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerChange = (val) => {
    setAnswers((prev) => ({
      ...prev,
      [activeIdx]: val
    }));
  };

  const toggleFlag = () => {
    setFlagged((prev) => ({
      ...prev,
      [activeIdx]: !prev[activeIdx]
    }));
  };

  const handleNext = () => {
    if (activeIdx < questions.length - 1) {
      setActiveIdx((prev) => prev + 1);
    } else {
      setShowSubmitDialog(true);
    }
  };

  const handlePrev = () => {
    if (activeIdx > 0) {
      setActiveIdx((prev) => prev - 1);
    }
  };

  const getAnsweredCount = () => {
    return questions.filter((q, idx) => {
      const ans = answers[idx];
      if (ans === undefined || ans === '') return false;
      return true;
    }).length;
  };

  const handleSubmitConfirm = async (isAutoSubmit = false) => {
    try {
      setIsSubmitting(true);
      if ((examType === 'MCQ' || questions.length > 0) && examIdFromUrl) {
        // Save timing for the current active question first
        const currentQ = questions[activeIdx];
        if (currentQ) {
          const elapsed = Math.round((getSynchronizedTime() - activeQuestionStartTimeRef.current) / 1000);
          if (elapsed > 0) {
            const ans = answers[activeIdx];
            let formattedAnswer = '';
            let targetQuestionId = currentQ.id.toString();

            if (ans !== undefined && ans !== '') {
              if (currentQ.type === 'MatchingSingle') {
                targetQuestionId = currentQ.parentQuestionId.toString();
                const parentAnswers = {};
                questions.forEach((otherQ, otherIdx) => {
                  if (otherQ.parentQuestionId === currentQ.parentQuestionId) {
                    const otherAns = answers[otherIdx];
                    if (otherAns !== undefined && otherAns !== '') {
                      parentAnswers[otherQ.leftItem.id] = otherAns;
                    }
                  }
                });
                formattedAnswer = JSON.stringify(parentAnswers);
              } else if (currentQ.type === 'FillBlank' || currentQ.type === 'TableOption') {
                formattedAnswer = ans.toString();
              } else {
                const selectedOpt = currentQ.options.find(o => o.id === ans);
                formattedAnswer = selectedOpt ? selectedOpt.order.toString() : ans.toString();
              }
            } else if (currentQ.type === 'MatchingSingle') {
              targetQuestionId = currentQ.parentQuestionId.toString();
              const parentAnswers = {};
              questions.forEach((otherQ, otherIdx) => {
                if (otherQ.parentQuestionId === currentQ.parentQuestionId) {
                  const otherAns = answers[otherIdx];
                  if (otherAns !== undefined && otherAns !== '') {
                    parentAnswers[otherQ.leftItem.id] = otherAns;
                  }
                }
              });
              if (Object.keys(parentAnswers).length > 0) {
                formattedAnswer = JSON.stringify(parentAnswers);
              }
            }

            try {
              await examService.saveQuestionAnswer(examIdFromUrl, {
                questionId: targetQuestionId,
                answer: formattedAnswer,
                durationSeconds: elapsed
              }, activeSessionTokenRef.current);
            } catch (err) {
              console.error('Failed to save final question progress:', err);
              if (err?.message?.includes('session_active_on_another_device') || err?.status === 409 || err?.data?.error_code === 'session_active_on_another_device') {
                setShowSubmitDialog(false);
                setForceDisconnectMessage('This session has been disconnected because you logged in or attended from another device/IP.');
                setShowForceDisconnectModal(true);
                return;
              } else if (err?.message === 'Exam expired') {
                if (!isAutoSubmit) {
                  setShowSubmitDialog(false);
                  handleExamExpired();
                  return;
                }
              }
            }
          }
        }

        // Build payload: { answers: { questionId: answer } }
        const answersPayload = {};
        questions.forEach((q, idx) => {
          const ans = answers[idx];
          if (ans === undefined || ans === '') return;

          if (q.type === 'MatchingSingle') {
            const parentId = q.parentQuestionId.toString();
            if (!answersPayload[parentId]) {
              const parentAnswers = {};
              questions.forEach((otherQ, otherIdx) => {
                if (otherQ.parentQuestionId === q.parentQuestionId) {
                  const otherAns = answers[otherIdx];
                  if (otherAns !== undefined && otherAns !== '') {
                    parentAnswers[otherQ.leftItem.id] = otherAns;
                  }
                }
              });
              if (Object.keys(parentAnswers).length > 0) {
                answersPayload[parentId] = JSON.stringify(parentAnswers);
              }
            }
          } else if (q.type === 'Matching' && q.tableJson && typeof ans === 'object') {
            // Invert map: stored as { rightSideId: leftSideId } → send as { leftSideId: rightSideId }
            const inverted = {};
            Object.entries(ans).forEach(([rightId, leftId]) => {
              if (leftId) inverted[leftId] = rightId;
            });
            answersPayload[q.id] = JSON.stringify(inverted);
          } else if (q.type === 'FillBlank' || q.type === 'TableOption') {
            answersPayload[q.id] = ans.toString();
          } else {
            // MCQ or TF — find the optionOrder for the selected optionId
            const selectedOpt = q.options && q.options.find(o => o.id === ans);
            answersPayload[q.id] = selectedOpt ? selectedOpt.order.toString() : ans.toString();
          }
        });

        const result = await examService.submitMcqExam(examIdFromUrl, { answers: answersPayload }, activeSessionTokenRef.current);
        if (result && result.content) {
          setSubmitResult(result.content);
        }
      } else if (examIdFromUrl) {
        // Essay or Structured exam submission
        const formData = new FormData();
        uploadedFiles.forEach((f) => {
          if (f.file) {
            formData.append('files', f.file);
          }
        });
        const result = await examService.submitEssayExam(examIdFromUrl, formData, activeSessionTokenRef.current);
        if (result && result.content) {
          setSubmitResult(result.content);
        }
      }
      setShowSubmitDialog(false);
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Failed to submit exam:', err);
      if (err?.message?.includes('session_active_on_another_device') || err?.status === 409 || err?.data?.error_code === 'session_active_on_another_device') {
        setShowSubmitDialog(false);
        setForceDisconnectMessage('This session has been disconnected because you logged in or attended from another device/IP.');
        setShowForceDisconnectModal(true);
      } else if (err?.message === 'Exam expired') {
        setShowSubmitDialog(false);
        handleExamExpired(false);
      } else if (
        err?.message === 'Exam duration has expired. You cannot submit.' ||
        err?.message?.includes('duration expired') ||
        err?.message?.includes('duration has expired') ||
        err?.message === 'You have already completed this exam'
      ) {
        setShowSubmitDialog(false);
        handleExamExpired(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const [expiredModalTitle, setExpiredModalTitle] = useState('Exam Expired!');
  const [expiredModalDescription, setExpiredModalDescription] = useState('This exam has expired. Your already done answers will be automatically submitted and graded, and you will be returned to the exams section.');

  const handleExamExpired = (isTimesUp = false) => {
    if (isTimesUp) {
      setExpiredModalTitle("Time's Up!");
      setExpiredModalDescription("Your exam duration has run out. Your answers will be automatically submitted and graded.");
    } else {
      setExpiredModalTitle("Exam Expired!");
      setExpiredModalDescription("This exam assignment has expired. Your already completed answers will be automatically submitted and graded.");
    }
    setShowExpiredModal(true);
  };

  const handleExpiredConfirm = () => {
    setShowExpiredModal(false);
    returnToCurrentExams();
  };

  const handleExitConfirm = () => {
    setShowExitDialog(false);
    if (pendingNavigationPath) {
      navigate(pendingNavigationPath);
      setPendingNavigationPath(null);
    } else {
      returnToCurrentExams();
    }
  };

  const handleFileUpload = (e) => {
    let files = [];
    if (e.target.files) files = Array.from(e.target.files);
    else if (e.dataTransfer && e.dataTransfer.files) files = Array.from(e.dataTransfer.files);

    if (files.length > 0) {
      const file = files[0];
      if (!isPdfFile(file)) {
        setUploadedFiles([]);
        setUploadError(PDF_UPLOAD_ERROR);
        resetFileInput(e.target);
        return;
      }

      setUploadError('');
      const newFileObj = {
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
        progress: 0,
        status: 'uploading',
        file: file
      };
      setUploadedFiles([newFileObj]);

      // Simulate upload progress
      let prog = 0;
      const interval = setInterval(() => {
        prog += 20;
        if (prog >= 100) {
          prog = 100;
          clearInterval(interval);
          setUploadedFiles([{ ...newFileObj, progress: 100, status: 'completed' }]);
        } else {
          setUploadedFiles([{ ...newFileObj, progress: prog, status: 'uploading' }]);
        }
      }, 200);
    }

    resetFileInput(e.target);
  };

  const handleRemoveFile = () => {
    setUploadedFiles([]);
    setUploadError('');
  };

  const currentQuestion = questions && questions.length > 0 ? questions[activeIdx] : null;
  const progressPercent = questions && questions.length > 0 ? (getAnsweredCount() / questions.length) * 100 : 0;
  const getTimerStatus = () => {
    if (isExamTimeOver) {
      if (uploadTimeLeft < 120) return 'critical';
      if (uploadTimeLeft < 600) return 'warning';
      return 'normal';
    } else {
      const totalSec = totalDurationSecRef.current || 5400;
      if (totalSec > 900) {
        if (timeLeft < 300) return 'critical';
        if (timeLeft < 900) return 'warning';
      } else {
        if (timeLeft < totalSec * 0.2) return 'critical';
        if (timeLeft < totalSec * 0.5) return 'warning';
      }
      return 'normal';
    }
  };

  const timerStatus = getTimerStatus();

  const getHeaderTimerStyles = () => {
    if (timerStatus === 'critical') {
      return {
        bgcolor: '#ef4444',
        border: '1.5px solid #dc2626',
        color: '#ffffff',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
        '@keyframes pulse': {
          '0%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.05)' },
          '100%': { transform: 'scale(1)' }
        },
        animation: 'pulse 1.2s infinite ease-in-out'
      };
    }
    if (timerStatus === 'warning') {
      return {
        bgcolor: '#f97316',
        border: '1.5px solid #ea580c',
        color: '#ffffff',
        boxShadow: 'none'
      };
    }
    return {
      bgcolor: 'rgba(255, 255, 255, 0.15)',
      border: '1.5px solid rgba(255, 255, 255, 0.3)',
      color: '#ffffff',
      boxShadow: 'none'
    };
  };

  const headerTimerStyles = getHeaderTimerStyles();

  if (
    !exam ||
    !exam.id ||
    examLoading ||
    (examType === 'MCQ' && (!questions || questions.length === 0))
  ) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 15 }}>
        <CircularProgress sx={{ color: '#FF502D' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 5, fontFamily: 'Poppins, sans-serif' }}>
      {/* Breadcrumbs */}
      <CustomBreadcrumbs items={breadcrumbItems} sx={{ mb: 2 }} />

      {!isOnline && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px', fontFamily: 'Poppins', fontWeight: 600 }}>
          You are currently offline. Your progress is being saved locally and will be automatically synced once you go back online.
        </Alert>
      )}
      {isSyncing && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: '12px', fontFamily: 'Poppins', fontWeight: 600 }}>
          Syncing your offline progress to the server...
        </Alert>
      )}

      {/* Page Banner */}
      <PageBanner
        title={getDisplayExamTitle(exam.name)}
        description={
          examType === 'MCQ' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
              <Typography sx={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.8)', fontWeight: 600, fontFamily: 'Poppins' }}>
                Progress:
              </Typography>
              <Box sx={{ width: 150 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressPercent}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: '#10b981',
                      borderRadius: 3
                    }
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: '13px', color: '#10b981', fontWeight: 700, fontFamily: 'Poppins' }}>
                {getAnsweredCount()} of {questions.length} Answered
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
              <Typography sx={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.8)', fontWeight: 600, fontFamily: 'Poppins' }}>
                Status:
              </Typography>
              <Typography sx={{ fontSize: '13px', color: uploadedFiles.length > 0 ? '#10b981' : '#f59e0b', fontWeight: 700, fontFamily: 'Poppins' }}>
                {uploadedFiles.length > 0 ? 'Answer Sheet Uploaded' : 'Pending Upload'}
              </Typography>
            </Box>
          )
        }
        backgroundImage={teacherBannerBg}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          {/* Live countdown timer widget */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              borderRadius: '16px',
              px: 2.5,
              py: 1.25,
              backdropFilter: 'blur(8px)',
              ...headerTimerStyles
            }}
          >
            <AccessTimeRoundedIcon sx={{ color: headerTimerStyles.color, fontSize: '20px' }} />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '15.5px',
                color: headerTimerStyles.color,
                fontFamily: 'Poppins',
                minWidth: '70px',
                letterSpacing: 0.5
              }}
            >
              {isExamTimeOver ? formatTime(uploadTimeLeft) : formatTime(timeLeft)}
            </Typography>
          </Box>

          {/* Exit button */}
          <IconButton
            onClick={() => setShowExitDialog(true)}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.15)',
              color: '#ffffff',
              width: 44,
              height: 44,
              borderRadius: '16px',
              border: '1.5px solid rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(8px)',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: '#ef4444',
                borderColor: '#ef4444',
                transform: 'scale(1.05)'
              }
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </PageBanner>

      {/* MCQ / Matching Split Grid or Essay/Structured Layout */}
      {examType === 'MCQ' ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2.3fr 1fr' },
            gap: 4,
            alignItems: 'flex-start'
          }}
        >
          {/* Left Column: Active Question Form Card */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              border: '1.5px solid #e2e8f0',
              borderRadius: '24px',
              bgcolor: '#ffffff',
              boxShadow: '0 8px 30px -10px rgba(100, 116, 139, 0.05)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box>
              {/* Question metadata badge row */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Chip
                  label={currentQuestion.title}
                  sx={{
                    bgcolor: '#FFF1EE',
                    color: '#FF502D',
                    fontWeight: 700,
                    fontSize: '12px',
                    fontFamily: 'Poppins',
                    borderRadius: '8px'
                  }}
                />
                <Typography sx={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600, fontFamily: 'Poppins' }}>
                  Question Type: {(currentQuestion.type === 'MCQ' || currentQuestion.type === 'TableOption') ? 'Multiple Choice' : currentQuestion.type === 'FillBlank' ? 'Fill in the Blanks' : currentQuestion.type === 'TF' ? 'True / False' : 'Matching 2 Sides'}
                </Typography>
              </Box>

              {/* Question Text */}
              <Box
                component="div"
                className="exam-rich-text"
                sx={{ fontWeight: 600, fontSize: '13px', color: '#1e293b', lineHeight: 1.55, mb: 2.5, fontFamily: 'Poppins', '& p': { m: 0 } }}
              >
                <LatexRenderer text={currentQuestion.text} />
              </Box>

              {/* Question Images */}
              {currentQuestion.questionImage && Array.isArray(currentQuestion.questionImage) && currentQuestion.questionImage.length > 0 && (
                <Box
                  sx={{
                    mb: 2.5,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    width: '100%'
                  }}
                >
                  {currentQuestion.questionImage.map((img, imgIdx) => {
                    const sizeStyles = getQuestionImageStyles(img.size);
                    return (
                      <Box key={imgIdx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <Box
                          component="img"
                          src={img.url}
                          alt={img.label || `Question image ${imgIdx + 1}`}
                          sx={{
                            ...sizeStyles,
                            width: 'auto',
                            height: 'auto',
                            borderRadius: '12px',
                            border: '1.5px solid #e2e8f0',
                            objectFit: 'contain'
                          }}
                        />
                        {img.label && (
                          <Typography sx={{ fontSize: '11.5px', color: '#94a3b8', fontWeight: 600, fontFamily: 'Poppins', fontStyle: 'italic' }}>
                            {img.label}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Render dynamic answer inputs based on question types */}
              {currentQuestion.type === 'MCQ' && (
                <Stack spacing={1} sx={{ mb: 1 }}>
                  {currentQuestion.options.map((opt, optIdx) => {
                    const optId = typeof opt === 'string' ? opt : opt.id;
                    const optText = typeof opt === 'string' ? opt : opt.text;
                    const isSelected = answers[activeIdx] === optId;
                    return (
                      <Box
                        key={optId}
                        onClick={() => handleAnswerChange(optId)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.5,
                          cursor: 'pointer',
                          borderRadius: '12px',
                          border: isSelected ? '2px solid #10b981' : '1.5px solid #e2e8f0',
                          bgcolor: isSelected ? '#ecfdf5' : '#ffffff',
                          transition: 'all 0.25s ease',
                          '&:hover': {
                            border: isSelected ? '2px solid #10b981' : '1.5px solid #cbd5e1',
                            bgcolor: isSelected ? '#ecfdf5' : '#f8fafc'
                          }
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '26px',
                              height: '26px',
                              borderRadius: '6px',
                              bgcolor: isSelected ? '#10b981' : '#f1f5f9',
                              color: isSelected ? '#ffffff' : '#475569',
                              fontWeight: 700,
                              fontSize: '12px',
                              fontFamily: 'Poppins',
                              flexShrink: 0
                            }}
                          >
                            {opt.order || (optIdx + 1)}
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1 }}>
                            {opt.image && (
                              <Box
                                component="img"
                                src={opt.image}
                                alt="Option"
                                sx={{
                                  maxWidth: '200px',
                                  maxHeight: '150px',
                                  width: 'auto',
                                  height: 'auto',
                                  borderRadius: '8px',
                                  objectFit: 'contain'
                                }}
                              />
                            )}
                            {(!opt.image || !/\.(png|jpe?g|gif|webp|svg)$/i.test(optText)) && (
                              <Box
                                component="div"
                                className="exam-rich-text"
                                sx={{ fontSize: '12.5px', fontWeight: 550, color: '#334155', fontFamily: 'Poppins', lineHeight: 1.5, '& p': { m: 0 } }}
                              >
                                <LatexRenderer text={optText} />
                              </Box>
                            )}
                          </Box>
                        </Stack>
                        {isSelected && <CheckCircleRoundedIcon sx={{ color: '#10b981', fontSize: '20px', ml: 1.5 }} />}
                      </Box>
                    );
                  })}
                </Stack>
              )}

              {currentQuestion.type === 'TableOption' && currentQuestion.tableJson && (() => {
                const rowKeys = Object.keys(currentQuestion.tableJson)
                  .filter(key => key.startsWith('row'))
                  .sort((a, b) => {
                    const numA = parseInt(a.replace('row', ''), 10);
                    const numB = parseInt(b.replace('row', ''), 10);
                    return numA - numB;
                  });

                const firstRowKey = rowKeys[0];
                const colKeys = firstRowKey
                  ? Object.keys(currentQuestion.tableJson[firstRowKey])
                    .filter(key => key.startsWith('col'))
                    .sort((a, b) => {
                      const numA = parseInt(a.replace('col', ''), 10);
                      const numB = parseInt(b.replace('col', ''), 10);
                      return numA - numB;
                    })
                  : [];

                return (
                  <Box className="exam-rich-text" sx={{ mt: 2, mb: 1 }}>
                    <Typography sx={{ fontSize: '12.5px', color: '#94a3b8', fontWeight: 600, mb: 2, fontFamily: 'Poppins' }}>
                      {currentQuestion.instruction || 'Select the correct row from the table below:'}
                    </Typography>
                    <Box className="exam-option-table-wrap">
                      <Box className="exam-option-table-header">
                        <Box className="exam-option-table-num-col" />
                        {colKeys.map((colKey, colIdx) => {
                          const headerVal = currentQuestion.tableJson[firstRowKey]?.[colKey]?.header || '';
                          return (
                            <Box
                              key={colKey}
                              className={`exam-option-table-cell${colIdx > 0 ? ' exam-option-table-cell-divider' : ''}`}
                            >
                              <LatexRenderer text={headerVal} />
                            </Box>
                          );
                        })}
                        <Box className="exam-option-table-check-col" />
                      </Box>
                      <Stack spacing={1} className="exam-option-table-body">
                        {rowKeys.map((rowKey, rowIdx) => {
                          const rowNumber = parseInt(rowKey.replace('row', ''), 10);
                          const isSelected = String(answers[activeIdx]) === String(rowNumber);
                          return (
                            <Box
                              key={rowKey}
                              className={`exam-option-table-row${isSelected ? ' exam-option-table-row-selected' : ''}`}
                              onClick={() => handleAnswerChange(rowNumber)}
                            >
                              <Box className="exam-option-table-num-col">
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '26px',
                                    height: '26px',
                                    borderRadius: '6px',
                                    bgcolor: isSelected ? '#10b981' : '#f1f5f9',
                                    color: isSelected ? '#ffffff' : '#475569',
                                    fontWeight: 700,
                                    fontSize: '12px',
                                    fontFamily: 'Poppins'
                                  }}
                                >
                                  {rowIdx + 1}
                                </Box>
                              </Box>
                              {colKeys.map((colKey, colIdx) => {
                                const cellVal = currentQuestion.tableJson[rowKey]?.[colKey]?.value || '';
                                return (
                                  <Box
                                    key={colKey}
                                    className={`exam-option-table-cell${colIdx > 0 ? ' exam-option-table-cell-divider' : ''}`}
                                  >
                                    <LatexRenderer text={cellVal} />
                                  </Box>
                                );
                              })}
                              <Box className="exam-option-table-check-col">
                                {isSelected && <CheckCircleRoundedIcon sx={{ color: '#10b981', fontSize: '20px' }} />}
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  </Box>
                );
              })()}

              {currentQuestion.type === 'FillBlank' && (
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: '12.5px', color: '#94a3b8', fontWeight: 600, mb: 1.5, fontFamily: 'Poppins' }}>
                    {currentQuestion.instruction || 'Type the correct answer clearly in the given space.'}
                  </Typography>

                  {/* Word bank / Given options display */}
                  {currentQuestion.givenAnswers && currentQuestion.givenAnswers.length > 0 && (
                    <Box sx={{ mb: 2.5, p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1.5px dashed #cbd5e1' }}>
                      <Typography sx={{ fontSize: '12.5px', color: '#64748b', fontWeight: 650, mb: 1, fontFamily: 'Poppins' }}>
                        Given Answers Word Bank:
                      </Typography>
                      <Box
                        component="div"
                        className="exam-rich-text"
                        sx={{ fontSize: '13.5px', color: '#334155', fontWeight: 550, fontFamily: 'Poppins', '& p': { m: 0 } }}
                      >
                        <LatexRenderer text={currentQuestion.givenAnswers.join(', ')} />
                      </Box>
                    </Box>
                  )}

                  <TextField
                    fullWidth
                    placeholder="Type your answer here..."
                    value={answers[activeIdx] || ''}
                    onChange={(e) => handleAnswerChange(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        '& fieldset': { borderColor: '#cbd5e1' },
                        '&:hover fieldset': { borderColor: '#94a3b8' },
                        '&.Mui-focused fieldset': { borderColor: '#4f46e5' }
                      }
                    }}
                  />
                </Box>
              )}

              {currentQuestion.type === 'TF' && (
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: '12.5px', color: '#94a3b8', fontWeight: 600, mb: 1.5, fontFamily: 'Poppins' }}>
                    {currentQuestion.instruction || 'Select True or False.'}
                  </Typography>
                  <Stack spacing={1.25}>
                    {(currentQuestion.options && currentQuestion.options.length > 0
                      ? currentQuestion.options
                      : ['True', 'False']
                    ).map((opt) => {
                      const optId = typeof opt === 'string' ? opt : opt.id;
                      const optText = typeof opt === 'string' ? opt : opt.text;
                      const isSelected = answers[activeIdx] === optId;
                      return (
                        <Box
                          key={optId}
                          onClick={() => handleAnswerChange(optId)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1.5,
                            cursor: 'pointer',
                            borderRadius: '12px',
                            border: isSelected ? '2px solid #10b981' : '1.5px solid #e2e8f0',
                            bgcolor: isSelected ? '#ecfdf5' : '#ffffff',
                            transition: 'all 0.25s ease',
                            '&:hover': {
                              border: isSelected ? '2px solid #10b981' : '1.5px solid #cbd5e1',
                              bgcolor: isSelected ? '#ecfdf5' : '#f8fafc'
                            }
                          }}
                        >
                          <Box
                            component="div"
                            className="exam-rich-text"
                            sx={{ fontSize: '12.5px', fontWeight: 550, color: '#334155', fontFamily: 'Poppins', lineHeight: 1.5, '& p': { m: 0 } }}
                          >
                            <LatexRenderer text={optText} />
                          </Box>
                          {isSelected && <CheckCircleRoundedIcon sx={{ color: '#10b981', fontSize: '20px' }} />}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {currentQuestion.type === 'MatchingSingle' && (
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: '13px', color: '#475569', fontWeight: 600, mb: 2.5, fontFamily: 'Poppins' }}>
                    Match this item from Side A with the correct corresponding item from Side B.
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Left side item (Side A) */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '16px',
                        border: '1.5px solid #7c3aed',
                        bgcolor: '#f5f3ff',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5
                      }}
                    >
                      <Chip
                        label={`Side A Item`}
                        size="small"
                        sx={{
                          bgcolor: '#7c3aed',
                          color: '#ffffff',
                          fontWeight: 700,
                          fontSize: '11px',
                          fontFamily: 'Poppins',
                          borderRadius: '6px',
                          alignSelf: 'flex-start'
                        }}
                      />
                      {currentQuestion.leftItem.image && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', my: 1 }}>
                          <Box
                            component="img"
                            src={currentQuestion.leftItem.image}
                            alt={`Side A Item`}
                            sx={{
                              maxWidth: '100%',
                              maxHeight: '220px',
                              objectFit: 'contain',
                              borderRadius: '8px',
                              border: '1.5px solid #e2e8f0'
                            }}
                          />
                        </Box>
                      )}
                      {currentQuestion.leftItem.value && (
                        <Typography
                          component="div"
                          className="exam-rich-text"
                          sx={{
                            fontSize: '14px',
                            color: '#1e293b',
                            fontWeight: 600,
                            fontFamily: 'Poppins, sans-serif',
                            '& p': { m: 0 }
                          }}
                        >
                          <LatexRenderer text={currentQuestion.leftItem.value} />
                        </Typography>
                      )}
                    </Paper>

                    {/* Right side options selector (Side B) */}
                    <Box>
                      <Typography sx={{ fontSize: '13.5px', color: '#475569', fontWeight: 700, mb: 1.5, fontFamily: 'Poppins' }}>
                        Choose Match from Side B:
                      </Typography>

                      <FormControl fullWidth size="medium">
                        <InputLabel sx={{ fontFamily: 'Poppins', fontSize: '14px' }}>Select Side B Option</InputLabel>
                        <Select
                          value={answers[activeIdx] || ''}
                          onChange={(e) => handleAnswerChange(e.target.value)}
                          label="Select Side B Option"
                          sx={{ borderRadius: '12px', fontFamily: 'Poppins', fontSize: '14px', bgcolor: '#ffffff' }}
                        >
                          {currentQuestion.rightSideOptions.map((item) => (
                            <MenuItem
                              key={item.id}
                              value={item.id}
                              sx={{ fontFamily: 'Poppins', fontSize: '14px', py: 1.5 }}
                            >
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#0284c7' }}>
                                  Option {item.id}
                                </Typography>
                                {item.image && (
                                  <Box component="img" src={item.image} alt="" sx={{ maxHeight: '60px', objectFit: 'contain' }} />
                                )}
                                {item.value && (
                                  <Typography component="div" className="exam-rich-text" sx={{ fontSize: '12px', '& p': { m: 0 } }}>
                                    <LatexRenderer text={item.value} />
                                  </Typography>
                                )}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Controls Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 1.5, borderTop: '1px solid #f1f5f9' }}>
              <Button
                onClick={handlePrev}
                disabled={activeIdx === 0}
                variant="outlined"
                startIcon={<NavigateBeforeRoundedIcon />}
                sx={{
                  borderRadius: '9999px',
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 3,
                  borderColor: '#cbd5e1',
                  color: '#64748b',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    bgcolor: '#f8fafc'
                  }
                }}
              >
                Previous
              </Button>

              <Button
                onClick={handleNext}
                variant="contained"
                sx={{
                  borderRadius: '9999px',
                  fontFamily: 'Poppins',
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 3.5,
                  background: 'linear-gradient(90deg, #FF8B4A 0%, #FF6244 100%)',
                  boxShadow: '0 8px 24px rgba(255, 98, 68, 0.35)',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #ff9b5f 0%, #ff7357 100%)',
                    boxShadow: '0 12px 28px rgba(255, 98, 68, 0.45)',
                    transform: 'translateY(-2px)'
                  },
                  '&.Mui-disabled': {
                    background: '#e2e8f0',
                    color: '#94a3b8',
                    boxShadow: 'none'
                  },
                  transition: 'all 0.25s ease'
                }}
              >
                {activeIdx === questions.length - 1 ? 'Submit Exam' : 'Next'}
              </Button>
            </Box>
          </Paper>

          {/* Right Column: Sidebar Map */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Question Grid Map Card */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1.5px solid #e2e8f0',
                borderRadius: '24px',
                bgcolor: '#ffffff',
                boxShadow: '0 8px 30px -10px rgba(100, 116, 139, 0.05)'
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '14.5px', color: '#1e293b', mb: 2.5, fontFamily: 'Poppins' }}>
                Question Navigator Map
              </Typography>

              <Box
                sx={{
                  mb: 3,
                  ...(questions.length > 54 && {
                    maxHeight: '415px',
                    overflowY: 'auto',
                    pr: 0.5,
                    '&::-webkit-scrollbar': {
                      width: '6px'
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f5f9',
                      borderRadius: '3px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#cbd5e1',
                      borderRadius: '3px',
                      '&:hover': {
                        background: '#94a3b8'
                      }
                    }
                  })
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    gap: 1.25
                  }}
                >
                  {questions.map((q, idx) => {
                    const isCurrent = activeIdx === idx;
                    let isAnswered = answers[idx] !== undefined && answers[idx] !== '';
                    if (isAnswered && q.type === 'Matching' && q.tableJson) {
                      isAnswered = Object.values(answers[idx]).some(val => val !== undefined && val !== '');
                    }
                    const isFlagged = flagged[idx];

                    // Determine border and background styling
                    let bgcolor = '#ffffff';
                    let color = '#64748b';
                    let borderColor = '#cbd5e1';

                    if (isAnswered) {
                      bgcolor = '#10b981';
                      color = '#ffffff';
                      borderColor = '#10b981';
                    }
                    if (isFlagged) {
                      bgcolor = '#f59e0b';
                      color = '#ffffff';
                      borderColor = '#f59e0b';
                    }
                    if (isCurrent) {
                      if (isAnswered) {
                        bgcolor = '#10b981';
                        color = '#ffffff';
                        borderColor = '#10b981';
                      } else if (isFlagged) {
                        bgcolor = '#f59e0b';
                        color = '#ffffff';
                        borderColor = '#f59e0b';
                      } else {
                        bgcolor = '#FFF1EE';
                        color = '#FF6244';
                        borderColor = '#FF6244';
                      }
                    }

                    const currentGlow = isAnswered
                      ? '0 0 0 2px rgba(16, 185, 129, 0.2)'
                      : isFlagged
                        ? '0 0 0 2px rgba(245, 158, 11, 0.2)'
                        : '0 0 0 2px rgba(255, 98, 68, 0.15)';

                    return (
                      <Box
                        key={q.id}
                        onClick={() => setActiveIdx(idx)}
                        sx={{
                          aspectRatio: '1',
                          borderRadius: '8px',
                          border: isCurrent ? '2px solid' : '1.2px solid',
                          borderColor,
                          bgcolor,
                          color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 700,
                          fontFamily: 'Poppins',
                          transition: 'all 0.2s',
                          boxShadow: isCurrent ? currentGlow : 'none',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.05)'
                          }
                        }}
                      >
                        {(idx + 1).toString().padStart(2, '0')}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Flag for review button */}
              <Button
                fullWidth
                variant="outlined"
                onClick={toggleFlag}
                startIcon={flagged[activeIdx] ? <FlagRoundedIcon /> : <FlagOutlinedIcon />}
                sx={{
                  borderRadius: '14px',
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  fontSize: '13px',
                  textTransform: 'none',
                  py: 1,
                  borderColor: flagged[activeIdx] ? '#f59e0b' : '#cbd5e1',
                  color: flagged[activeIdx] ? '#f59e0b' : '#64748b',
                  bgcolor: flagged[activeIdx] ? 'rgba(245, 158, 11, 0.05)' : 'transparent',
                  '&:hover': {
                    borderColor: flagged[activeIdx] ? '#d97706' : '#94a3b8',
                    bgcolor: flagged[activeIdx] ? 'rgba(245, 158, 11, 0.1)' : '#f8fafc'
                  }
                }}
              >
                {flagged[activeIdx] ? 'Flagged for Review' : 'Flag for Review'}
              </Button>
            </Paper>

            {/* Quick Submit Exam Panel Card */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1.5px solid #e2e8f0',
                borderRadius: '24px',
                bgcolor: '#ffffff',
                boxShadow: '0 8px 30px -10px rgba(100, 116, 139, 0.05)',
                textAlign: 'center'
              }}
            >
              <Typography sx={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600, fontFamily: 'Poppins', mb: 2 }}>
                Make sure to double check all your answers.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={() => setShowSubmitDialog(true)}
                sx={{
                  borderRadius: '14px',
                  fontFamily: 'Poppins',
                  fontWeight: 700,
                  textTransform: 'none',
                  py: 1.25,
                  background: 'linear-gradient(90deg, #FF8B4A 0%, #FF6244 100%)',
                  boxShadow: '0 6px 16px rgba(255, 98, 68, 0.25)',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #ff9b5f 0%, #ff7357 100%)',
                    boxShadow: '0 8px 20px rgba(255, 98, 68, 0.35)'
                  },
                  '&.Mui-disabled': {
                    background: '#e2e8f0',
                    color: '#94a3b8',
                    boxShadow: 'none'
                  }
                }}
              >
                Submit Exam
              </Button>
            </Paper>
          </Box>
        </Box>
      ) : (
        <EssayExamPaperSection
          examId={exam?.examId}
          uploadedFiles={uploadedFiles}
          uploadError={uploadError}
          handleFileUpload={handleFileUpload}
          handleRemoveFile={handleRemoveFile}
          onSubmitConfirm={() => setShowSubmitDialog(true)}
          isExamTimeOver={isExamTimeOver}
          uploadTimeLeft={uploadTimeLeft}
          formatTime={formatTime}
        />
      )}

      {/* Confirmation Dialog for Exiting */}
      <Dialog
        open={showExitDialog}
        onClose={() => {
          setShowExitDialog(false);
          setPendingNavigationPath(null);
        }}
        disableScrollLock
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(15, 23, 42, 0.4)',
            backdropFilter: 'blur(8px)',
          },
          '& .MuiPaper-root': {
            borderRadius: '24px !important',
            width: '100%',
            maxWidth: '400px',
            margin: '16px',
            boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            fontFamily: "'Poppins', sans-serif",
            overflow: 'visible',
            position: 'relative',
            p: { xs: 3, sm: 4 },
            pt: { xs: 4, sm: 5 }
          }
        }}
      >
        <DialogContent sx={{ p: 0, textAlign: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={dangerIcon}
              alt="Warning"
              sx={{
                width: '90px',
                height: '90px',
                mb: 3,
                filter: 'drop-shadow(0 8px 16px rgba(204, 70, 70, 0.15))'
              }}
            />

            <Typography
              variant="h5"
              sx={{
                fontWeight: 750,
                color: '#1e293b',
                mb: 2,
                fontSize: { xs: '20px', sm: '22px' },
                fontFamily: "'Poppins', sans-serif"
              }}
            >
              Cancel Quiz
            </Typography>

            <Typography
              sx={{
                fontSize: '13.5px',
                color: '#64748b',
                fontFamily: "'Poppins', sans-serif",
                lineHeight: 1.5,
                mb: 4,
                px: 2
              }}
            >
              Are you sure you want to cancel this quiz? If you cancel, your progress will not be saved.
            </Typography>

            <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'center' }}>
              <Button
                onClick={() => {
                  setShowExitDialog(false);
                  setPendingNavigationPath(null);
                }}
                variant="outlined"
                sx={{
                  flex: 1,
                  borderRadius: '9999px',
                  py: 1.5,
                  fontSize: '14px',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: "'Poppins', sans-serif",
                  borderColor: '#CC4646',
                  color: '#CC4646',
                  borderWidth: '1.5px',
                  '&:hover': {
                    borderColor: '#9c1717',
                    bgcolor: 'rgba(204, 70, 70, 0.05)',
                    borderWidth: '1.5px'
                  }
                }}
              >
                Continue Quiz
              </Button>
              <Button
                onClick={handleExitConfirm}
                variant="contained"
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #CC4646 0%, #9C1717 100%)',
                  color: '#ffffff',
                  borderRadius: '9999px',
                  py: 1.5,
                  fontSize: '14px',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: "'Poppins', sans-serif",
                  '&:hover': {
                    background: 'linear-gradient(135deg, #e05c5c 0%, #b32424 100%)',
                  },
                  '&:active': {
                    transform: 'translateY(0)'
                  },
                  transition: 'all 0.25s ease'
                }}
              >
                Cancel Quiz
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Submitting */}
      {examType === 'MCQ' ? (
        <SubmitProgressModal
          isOpen={showSubmitDialog}
          onClose={() => !isSubmitting && setShowSubmitDialog(false)}
          onSubmitConfirm={handleSubmitConfirm}
          isLoading={isSubmitting}
          questions={questions}
          answers={answers}
          onNavigateToQuestion={(idx) => setActiveIdx(idx)}
        />
      ) : (
        <SubmitExamModal
          isOpen={showSubmitDialog}
          onClose={() => !isSubmitting && setShowSubmitDialog(false)}
          onSubmitConfirm={handleSubmitConfirm}
          isLoading={isSubmitting}
        />
      )}

      {/* Exam Expired Modal */}
      <ExamExpiredModal
        isOpen={showExpiredModal}
        onClose={handleExpiredConfirm}
        title={expiredModalTitle}
        description={expiredModalDescription}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          if (examType === 'MCQ') {
            setShowResultModal(true);
          } else {
            returnToCurrentExams();
          }
        }}
        title="Exam Submitted Successfully!"
        description="Congratulations! Your exam paper has been submitted successfully. You can view your results in the graded exams panel once the grading is complete."
        actionText="Okay"
        onAction={() => {
          setShowSuccessModal(false);
          if (examType === 'MCQ') {
            setShowResultModal(true);
          } else {
            returnToCurrentExams();
          }
        }}
      />

      {/* Exam Result Modal */}
      <ExamResultModal
        isOpen={showResultModal}
        onClose={() => {
          setShowResultModal(false);
          returnToCurrentExams();
        }}
        submitResult={submitResult}
        examName={exam.name}
        className="A/L Bio Class"
        onCheckPaper={() => {
          setShowResultModal(false);
          const summary = submitResult?.gradingSummary || {};
          const detailedResults = submitResult?.detailedResults || [];

          navigate(`/exam-manager/exam-summary/${examIdFromUrl}`, {
            state: {
              exam,
              isCompleted: true,
              results: {
                answers,
                questions: questions,
                detailedResults,
                totalCount: summary.totalQuestions || questions.length,
                answeredCount: detailedResults.filter(r => r.studentAnswer != null && r.studentAnswer !== '').length,
                correctCount: summary.correctCount || 0,
                skippedCount: Math.max(0, (summary.totalQuestions || 0) - detailedResults.filter(r => r.studentAnswer != null && r.studentAnswer !== '').length),
                wrongCount: Math.max(0, (summary.incorrectCount || 0) - Math.max(0, (summary.totalQuestions || 0) - detailedResults.filter(r => r.studentAnswer != null && r.studentAnswer !== '').length)),
                scorePercentage: summary.percentage || 0,
                totalMarksAwarded: summary.totalMarksAwarded || 0,
                totalMarksPossible: summary.totalMarksPossible || 0,
                grade: summary.grade || '-'
              }
            }
          });
        }}
      />

      {/* Force Disconnect Dialog */}
      <Dialog
        open={showForceDisconnectModal}
        disableEscapeKeyDown
        onClose={() => { }}
        disableScrollLock
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(15, 23, 42, 0.4)',
            backdropFilter: 'blur(8px)',
          },
          '& .MuiPaper-root': {
            borderRadius: '24px !important',
            width: '100%',
            maxWidth: '400px',
            margin: '16px',
            boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            fontFamily: "'Poppins', sans-serif",
            overflow: 'visible',
            position: 'relative',
            p: { xs: 3, sm: 4 },
            pt: { xs: 4, sm: 5 }
          }
        }}
      >
        <DialogContent sx={{ p: 0, textAlign: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={dangerIcon}
              alt="Warning"
              sx={{
                width: '90px',
                height: '90px',
                mb: 3,
                filter: 'drop-shadow(0 8px 16px rgba(204, 70, 70, 0.15))'
              }}
            />

            <Typography
              variant="h5"
              sx={{
                fontWeight: 750,
                color: '#1e293b',
                mb: 2,
                fontSize: { xs: '20px', sm: '22px' },
                fontFamily: "'Poppins', sans-serif"
              }}
            >
              Session Disconnected
            </Typography>

            <Typography
              sx={{
                fontSize: '13.5px',
                color: '#64748b',
                fontFamily: "'Poppins', sans-serif",
                lineHeight: 1.6,
                mb: 4,
                px: 1
              }}
            >
              {forceDisconnectMessage}
            </Typography>

            <Button
              onClick={() => {
                setShowForceDisconnectModal(false);
                navigate('/exam-manager');
              }}
              variant="contained"
              fullWidth
              sx={{
                background: 'linear-gradient(135deg, #CC4646 0%, #9C1717 100%)',
                color: '#ffffff',
                borderRadius: '9999px',
                py: 1.5,
                fontSize: '15px',
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: "'Poppins', sans-serif",
                '&:hover': {
                  background: 'linear-gradient(135deg, #e05c5c 0%, #b32424 100%)',
                },
                '&:active': {
                  transform: 'translateY(0)'
                },
                transition: 'all 0.25s ease'
              }}
            >
              Go to Dashboard
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
